import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GripVertical, ArrowLeftRight } from 'lucide-react';
import ChaoticAnimation from './ChaoticAnimation';
import ClarityAnimation from './ClarityAnimation';

export default function BeforeAfterSlider() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(5, Math.min(95, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    handleMove(touch.clientX);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Handle click on the container to move slider
  const handleContainerClick = (e) => {
    if (e.target.closest('[data-slider-handle]')) return;
    handleMove(e.clientX);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging]);

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Hook text */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-center mb-4"
      >
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium">
          <ArrowLeftRight className="h-4 w-4" />
          Skip expensive lessons - drag to compare
        </span>
      </motion.div>

      {/* Slider Container */}
      <motion.div
        ref={containerRef}
        data-testid="before-after-slider"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative w-full h-[280px] md:h-[320px] rounded-2xl overflow-hidden border border-slate-200 shadow-lg cursor-ew-resize select-none"
        onClick={handleContainerClick}
      >
        {/* "Before" (Chaos) - Full width background */}
        <div className="absolute inset-0">
          <ChaoticAnimation />
        </div>

        {/* "After" (Clarity) - Clipped based on slider position, reveals from right */}
        <div 
          className="absolute inset-0"
          style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
        >
          <ClarityAnimation />
        </div>

        {/* Slider Handle */}
        <div
          data-slider-handle
          data-testid="slider-handle"
          className="absolute top-0 bottom-0 z-20"
          style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Vertical line */}
          <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-white -translate-x-1/2 shadow-md" />
          
          {/* Handle button */}
          <motion.div
            className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center cursor-grab ${
              isDragging ? 'cursor-grabbing scale-110' : ''
            } transition-transform duration-150`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 1.15 }}
          >
            <GripVertical className="h-5 w-5 text-slate-700" />
          </motion.div>

          {/* Labels on handle */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-4">
            <span className="px-2 py-1 text-xs font-semibold bg-red-500 text-white rounded-full whitespace-nowrap shadow-sm">
              Before
            </span>
            <span className="px-2 py-1 text-xs font-semibold bg-blue-600 text-white rounded-full whitespace-nowrap shadow-sm">
              After
            </span>
          </div>
        </div>
      </motion.div>

      {/* Instructions */}
      <p className="text-center mt-3 text-sm text-slate-500">
        Drag the slider to see the transformation
      </p>
    </div>
  );
}
